import React from 'react';
import { formatDate } from '@/utils';

interface WorkflowStage {
  stageName: string;
  approvalGroups?: Array<{
    _id: string;
    name: string;
    members?: string[];
  }>;
}

interface Workflow {
  _id: string;
  name: string;
  description?: string;
  stages?: WorkflowStage[];
}

interface Approver {
  _id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

interface ReleaseRequest {
  _id: string;
  userId: string | { _id: string; username?: string; firstName?: string; lastName?: string; email?: string };
  requestedAt: string;
  reason?: string;
  status: 'pending' | 'granted' | 'denied';
}

interface DraftLock {
  isLocked: boolean;
  lockedBy?: string | { _id: string; username?: string; firstName?: string; lastName?: string; email?: string };
  lockedAt?: string;
  lockExpiry?: string;
  releaseRequests?: ReleaseRequest[];
}

interface WorkflowProgressTrackerProps {
  workflow: Workflow | null | undefined;
  currentStage?: string;
  status?: string;
  currentStageApprovers?: Approver[];
  stageTransitionedAt?: string;
  canApprove?: boolean;
  canReject?: boolean;
  onApprove?: () => void;
  onReject?: () => void;
  draftLock?: DraftLock;
  canEdit?: boolean;
  onRequestRelease?: () => void;
  onGrantRelease?: (requestId: string) => void;
  onDenyRelease?: (requestId: string) => void;
}

export const WorkflowProgressTracker: React.FC<WorkflowProgressTrackerProps> = ({
  workflow,
  currentStage,
  status,
  currentStageApprovers = [],
  stageTransitionedAt,
  canApprove = false,
  canReject = false,
  onApprove,
  onReject,
  draftLock,
  canEdit = false,
  onRequestRelease,
  onGrantRelease,
  onDenyRelease
}) => {
  const getLockedByName = () => {
    if (!draftLock?.lockedBy) return 'Unknown User';

    const lockedBy = draftLock.lockedBy;
    if (typeof lockedBy === 'string') return lockedBy;

    if (lockedBy.firstName && lockedBy.lastName) {
      return `${lockedBy.firstName} ${lockedBy.lastName}`;
    }
    return lockedBy.username || lockedBy.email || 'Unknown User';
  };

  const getRequesterName = (userId: ReleaseRequest['userId']) => {
    if (typeof userId === 'string') return 'Unknown User';

    if (userId.firstName && userId.lastName) {
      return `${userId.firstName} ${userId.lastName}`;
    }
    return userId.username || userId.email || 'Unknown User';
  };
  if (!workflow) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Workflow Progress
        </h2>
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <p>No workflow assigned</p>
          <p className="text-sm mt-2">This is a manual response</p>
        </div>
      </div>
    );
  }

  const stages = workflow.stages || [];
  const currentStageIndex = stages.findIndex((s) => (s.stageName || s.name) === currentStage);

  const getStageStatus = (index: number): 'completed' | 'current' | 'pending' => {
    if (index < currentStageIndex) return 'completed';
    if (index === currentStageIndex) return 'current';
    return 'pending';
  };

  const getStatusColor = (stageStatus: 'completed' | 'current' | 'pending') => {
    switch (stageStatus) {
      case 'completed':
        return 'bg-green-500';
      case 'current':
        return 'bg-blue-500';
      case 'pending':
        return 'bg-gray-300 dark:bg-gray-600';
    }
  };

  const getStatusTextColor = (stageStatus: 'completed' | 'current' | 'pending') => {
    switch (stageStatus) {
      case 'completed':
        return 'text-green-700 dark:text-green-300';
      case 'current':
        return 'text-blue-700 dark:text-blue-300';
      case 'pending':
        return 'text-gray-500 dark:text-gray-400';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Workflow Progress
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          {workflow.name}
        </p>
      </div>

      {/* Stages */}
      <div className="p-4 space-y-4">
        {stages.map((stage, index) => {
          const stageStatus = getStageStatus(index);
          const isCurrent = stageStatus === 'current';

          return (
            <div key={stage.stageName || stage.name} className="relative">
              {/* Connector Line */}
              {index < stages.length - 1 && (
                <div className="absolute left-4 top-10 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700" />
              )}

              {/* Stage Item */}
              <div className="relative flex items-start space-x-3">
                {/* Status Icon */}
                <div className="relative z-10">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${getStatusColor(
                      stageStatus
                    )}`}
                  >
                    {stageStatus === 'completed' ? (
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : stageStatus === 'current' ? (
                      <div className="w-3 h-3 bg-white rounded-full" />
                    ) : (
                      <div className="w-3 h-3 bg-gray-400 dark:bg-gray-500 rounded-full" />
                    )}
                  </div>
                </div>

                {/* Stage Content */}
                <div className="flex-1 min-w-0">
                  <div
                    className={`font-medium ${
                      isCurrent
                        ? 'text-blue-900 dark:text-blue-100'
                        : stageStatus === 'completed'
                        ? 'text-gray-900 dark:text-gray-100'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {stage.stageName || stage.name}
                  </div>

                  {/* Approval Groups */}
                  {stage.approvalGroups && stage.approvalGroups.length > 0 && (
                    <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                      {stage.approvalGroups.map((group) => (
                        <span
                          key={group._id}
                          className="inline-flex items-center px-2 py-0.5 rounded mr-1 mb-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                        >
                          {group.name}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Current Stage Info */}
                  {isCurrent && (
                    <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <div className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                        Currently at this stage
                      </div>

                      {stageTransitionedAt && (
                        <p className="text-xs text-blue-700 dark:text-blue-300 mb-2">
                          Since {formatDate(stageTransitionedAt)}
                        </p>
                      )}

                      {/* Draft Lock Status */}
                      {draftLock?.isLocked && (
                        <div className="mb-3 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <div className="flex items-center gap-2">
                              <svg className="w-4 h-4 text-amber-600 dark:text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                              </svg>
                              <p className="text-xs font-semibold text-amber-900 dark:text-amber-100">
                                Draft Locked
                              </p>
                            </div>
                            {!canEdit && onRequestRelease && (
                              <button
                                onClick={onRequestRelease}
                                className="text-xs px-2 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded transition-colors"
                              >
                                Request Release
                              </button>
                            )}
                          </div>
                          <p className="text-xs text-amber-800 dark:text-amber-200">
                            {canEdit ? (
                              <>You have the draft lock</>
                            ) : (
                              <>
                                Locked by <span className="font-medium">{getLockedByName()}</span>
                                {draftLock.lockExpiry && (
                                  <span className="ml-1">
                                    • Expires at {new Date(draftLock.lockExpiry).toLocaleString()}
                                  </span>
                                )}
                              </>
                            )}
                          </p>
                        </div>
                      )}

                      {/* Release Requests for Lock Owner */}
                      {canEdit && draftLock?.releaseRequests && draftLock.releaseRequests.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-2">
                            Release Requests ({draftLock.releaseRequests.filter(r => r.status === 'pending').length} pending)
                          </p>
                          <div className="space-y-2">
                            {draftLock.releaseRequests
                              .filter(r => r.status === 'pending')
                              .map((request) => (
                                <div
                                  key={request._id}
                                  className="p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded"
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1">
                                      <p className="text-xs font-medium text-blue-900 dark:text-blue-100">
                                        {getRequesterName(request.userId)}
                                      </p>
                                      <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                                        {new Date(request.requestedAt).toLocaleString()}
                                      </p>
                                      {request.reason && (
                                        <p className="text-xs text-blue-800 dark:text-blue-200 mt-1 italic">
                                          "{request.reason}"
                                        </p>
                                      )}
                                    </div>
                                    <div className="flex gap-1">
                                      {onGrantRelease && (
                                        <button
                                          onClick={() => onGrantRelease(request._id)}
                                          className="text-xs px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
                                          title="Grant release and unlock draft"
                                        >
                                          Grant
                                        </button>
                                      )}
                                      {onDenyRelease && (
                                        <button
                                          onClick={() => onDenyRelease(request._id)}
                                          className="text-xs px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                                          title="Deny request"
                                        >
                                          Deny
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}

                      {currentStageApprovers.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs text-blue-700 dark:text-blue-300 mb-1">
                            Approvers:
                          </p>
                          <div className="space-y-1">
                            {currentStageApprovers.map((approver) => (
                              <div
                                key={approver._id}
                                className="text-xs text-gray-700 dark:text-gray-300"
                              >
                                {approver.firstName} {approver.lastName}
                                {approver.email && ` (${approver.email})`}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      {(canApprove || canReject) && (
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-2">
                            Available Actions:
                          </p>
                          <div className="flex flex-col space-y-2">
                            {canApprove && (
                              <button
                                onClick={onApprove}
                                className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md transition-colors flex items-center justify-center gap-2"
                              >
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                <span>Approve</span>
                              </button>
                            )}
                            {canReject && (
                              <button
                                onClick={onReject}
                                className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-md transition-colors flex items-center justify-center gap-2"
                              >
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path
                                    fillRule="evenodd"
                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                <span>Reject</span>
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Overall Status */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600 dark:text-gray-400">Overall Status:</span>
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              status?.toString() === 'Draft'
                ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                : status?.toString() === 'Approved'
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                : status?.toString() === 'Rejected'
                ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                : status?.toString() === 'Sent'
                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
            }`}
          >
            {status}
          </span>
        </div>
      </div>
    </div>
  );
};
